import { InvestorRelationsSite } from '@assiduous/investor-relations-site'
import { Box, Flex, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  IRDocument,
  IRFundraisingConfig,
  IRResultsExtras,
  InvestorRelationsConfig,
} from '@/utils/investorRelations'
import {
  getCompanySlug,
  getDocumentDeepLink,
  loadPublicIrSite,
  resolveReleaseUrl,
} from './releaseLoader'
import type { LoadedPublicIrSite, PublicIrDocument } from './types'

const EMPTY_DOCUMENTS: IRDocument[] = []

function mapDocument(
  document: PublicIrDocument,
  resolveUrl: (path?: string | null) => string | null
): IRDocument {
  const publicUrl = resolveUrl(document.url) || undefined
  return {
    attachmentId: document.attachmentId || document.id,
    fileId: document.id,
    fileName: document.fileName,
    fileType: document.fileType,
    fileSize: document.fileSize,
    createdAt: document.createdAt || document.publishedDate || '',
    updatedAt: document.updatedAt || document.publishedDate || '',
    publishedDate: document.publishedDate || '[Not published]',
    labels: document.labels || [],
    attachmentType: document.attachmentType || document.fileType,
    duration: document.duration || undefined,
    publicUrl,
    thumbnailUrl: publicUrl,
  }
}

export function PublicIrApp() {
  const companySlug = getCompanySlug(window.location.pathname)
  const [loaded, setLoaded] = useState<LoadedPublicIrSite | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [deepLink, setDeepLink] = useState(() =>
    getDocumentDeepLink(window.location.pathname)
  )

  useEffect(() => {
    if (!companySlug) {
      setError(new Error('Choose a published company website to continue.'))
      return
    }
    loadPublicIrSite(companySlug)
      .then(setLoaded)
      .catch((reason: unknown) => {
        setError(
          reason instanceof Error
            ? reason
            : new Error('The website could not be loaded.')
        )
      })
  }, [companySlug])

  useEffect(() => {
    const updateDeepLink = () =>
      setDeepLink(getDocumentDeepLink(window.location.pathname))
    window.addEventListener('popstate', updateDeepLink)
    return () => window.removeEventListener('popstate', updateDeepLink)
  }, [])

  const releaseUrl = useCallback(
    (path?: string | null) =>
      loaded ? resolveReleaseUrl(loaded.siteDocumentUrl, path) : null,
    [loaded]
  )

  const mapped = useMemo(() => {
    if (!loaded) return null
    const mapSection = (section: string) =>
      (loaded.site.documents[section] || []).map((document) =>
        mapDocument(document, releaseUrl)
      )
    const { site } = loaded
    const fundraising = site.fundraising
      ? ({
          ...site.fundraising,
          generalInfo: {
            offerOpenFrom: '',
            offerClosing: '',
            ...site.fundraising.generalInfo,
          },
          presentation: {
            documentIds: [],
            ...site.fundraising.presentation,
            badgeUrl: releaseUrl(site.fundraising.presentation?.badgeUrl),
            managementTeamImageUrl: releaseUrl(
              site.fundraising.presentation?.managementTeamImageUrl
            ),
          },
          terms: { documentIds: [] },
          documents: { documentIds: [] },
          qa: site.fundraising.qa || [],
        } as IRFundraisingConfig)
      : undefined
    const resultsExtras = site.resultsExtras
      ? ({
          video: {
            mode: null,
            ...site.resultsExtras.video,
            publishedUrl: releaseUrl(site.resultsExtras.video?.publishedUrl),
          },
          qa: site.resultsExtras.qa || [],
        } as IRResultsExtras)
      : undefined

    const config: InvestorRelationsConfig = {
      companySlugPreview: site.companySlug,
      companySlugPublished: site.companySlug,
      companyConfig: {
        companyName: site.company.companyName,
        logoUrl: releaseUrl(site.company.logoUrl),
        companyWebsite: site.company.companyWebsite || '',
        companyDescription: site.company.companyDescription || '',
        companyLinkedinUrl: site.company.companyLinkedinUrl,
        companyYoutubeUrl: site.company.companyYoutubeUrl,
        companyXUrl: site.company.companyXUrl,
        companyFacebookUrl: site.company.companyFacebookUrl,
        companyInstagramUrl: site.company.companyInstagramUrl,
        irContactName: site.company.irContactName,
        irContactEmail: site.company.irContactEmail || '',
        companyStockTicker: site.company.companyStockTicker || '',
        companyStockExchange: site.company.companyStockExchange || '',
        sharePriceLink: site.company.sharePriceLink || '',
      },
      themeConfig: {
        title: site.theme.title || '',
        subtitle: site.theme.subtitle || '',
        primaryColor: site.theme.primaryColor || '#17384d',
        textColor: site.theme.textColor || '#24313a',
        titleAndSubtitleColor: site.theme.titleAndSubtitleColor || '#ffffff',
        fontStyle: site.theme.fontStyle || 'Plus Jakarta Sans, sans-serif',
        heroImageUrl: releaseUrl(site.theme.heroImageUrl),
        heroImageUrls: (site.theme.heroImageUrls || [])
          .map(releaseUrl)
          .filter((url): url is string => Boolean(url)),
        heroCarouselSpeedSeconds: site.theme.heroCarouselSpeedSeconds,
      },
      documentConfig: {
        corporate: [],
        regulatory: [],
        reports: { documents: [], resultsExtras },
        documents: [],
      },
      fundraisingConfig: fundraising,
    }

    return {
      config,
      documents: {
        corporate: mapSection('corporate'),
        regulatory: mapSection('regulatory'),
        reports: mapSection('reports'),
        documents: mapSection('documents'),
        fundraisingPresentation: mapSection('fundraising-presentation'),
        fundraisingTerms: mapSection('fundraising-terms'),
        fundraisingDocuments: mapSection('fundraising-documents'),
      },
    }
  }, [loaded, releaseUrl])

  const basePath = companySlug
    ? window.location.pathname.startsWith('/investor-relations/')
      ? `/investor-relations/${companySlug}`
      : `/${companySlug}`
    : '/'

  const handleDocumentOpen = useCallback(
    (sectionId: string, documentId: string) => {
      window.history.pushState(
        null,
        '',
        `${basePath}/${sectionId}/${encodeURIComponent(documentId)}`
      )
      setDeepLink({ section: sectionId, documentId })
    },
    [basePath]
  )

  const handleDocumentClose = useCallback(
    (sectionId: string) => {
      window.history.replaceState(null, '', `${basePath}#${sectionId}`)
      setDeepLink(null)
    },
    [basePath]
  )

  if (error) {
    return (
      <Flex minH="100vh" align="center" justify="center" p={8}>
        <VStack gap={4} textAlign="center">
          <Heading>Website unavailable</Heading>
          <Text>{error.message}</Text>
        </VStack>
      </Flex>
    )
  }

  if (!mapped || !companySlug) {
    return (
      <Box
        display="flex"
        minH="100vh"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <InvestorRelationsSite
      config={mapped.config}
      documents={mapped.documents}
      deepLinkedDocumentId={deepLink?.documentId || null}
      deepLinkedSectionId={deepLink?.section || null}
      onDocumentOpen={handleDocumentOpen}
      onDeepLinkedDocumentClose={handleDocumentClose}
    />
  )
}
